import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, ChevronDown, ChevronUp, Package, ShoppingCart, MessageCircle, AlertTriangle, ExternalLink, Loader2, Download, DollarSign, RefreshCw, ArrowLeft } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { api } from '../../api/client';
import { Can, useCan } from '../../lib/rbac';

// ── Types ──

interface ConversationSummary {
  pack_id: number;
  ml_order_id: number;
  buyer_nickname: string;
  buyer_id: number;
  date_created: string;
  last_updated: string;
  status: string;
  paid_amount: number;
  currency_id: string;
  context_site: string;
  item_title: string | null;
  item_thumbnail: string | null;
  item_id: string | null;
  seller_sku: string | null;
  ship_status: string | null;
  ship_tracking: string | null;
  amazon_status: string | null;
  amazon_order_id: string | null;
  unread_count: number;
  msg_count: number;
  last_message_date: string | null;
  last_message_text: string | null;
  last_message_from: number | null;
}

interface MLMessage {
  id: string;
  from: { user_id: number };
  to: { user_id: number };
  text: string;
  text_translated: string | null;
  message_date: { created: string; read: string | null };
  message_moderation: { status: string };
  message_attachments: { filename: string; original_filename: string; type: string; size: number }[];
}

interface ConversationDetail {
  messages: { conversation_status: { status: string; substatus: string | null }; messages: MLMessage[] } | null;
  order: {
    ml_order_id: number; status: string; paid_amount: number; currency_id: string;
    date_created: string; context_site: string; buyer_nickname: string; buyer_id: number;
    items: { title: string | null; unit_price: number; quantity: number; sale_fee: number; item_id: string; seller_sku: string; thumbnail: string | null }[];
  };
  shipment: { status: string | null; tracking_number: string | null } | null;
  claim: { ml_claim_id: number; status: string; type: string; stage: string } | null;
  amazon_purchase: { asin: string; amazon_status: string; total_cost: number | null; tracking_number: string | null; amazon_order_id: string | null; carrier: string | null } | null;
  stock_po: { po_number: string; status: string; fulfillment_status: string } | null;
  amazon_product: { title: string; brand: string | null; price: number | null; category: string | null } | null;
  profit: number | null;
  margin_pct: number | null;
  buyer_questions: { question_id: number; text: string; answer_text: string | null; date_created: string; item_id: string }[];
  buyer_orders: { ml_order_id: number; date_created: string; status: string; paid_amount: number; currency_id: string; item_title: string | null }[];
}

// ── Main Component ──

export default function MessagesPage() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();
  const isMobile = useIsMobile();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loading, setLoading] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('sale');
  const can = useCan();
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  const threadEndRef = useRef<HTMLDivElement>(null);

  // ── Load conversations ──
  const loadConversations = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    try {
      const res = await api.get<ConversationSummary[]>(
        `/mercadolibre/messages/conversations?seller_id=${sellerId}&filter=${filter}&limit=100`
      );
      if (res.success && res.data) {
        setConversations(res.data);
        setTotal((res as any).meta?.total || 0);
        setUnreadTotal((res as any).meta?.unread_total || 0);
      }
    } catch { /* silenced */ }
    setLoading(false);
  }, [sellerId, filter]);

  // ── Load detail ──
  const loadDetail = useCallback(async (packId: number) => {
    if (!sellerId) return;
    setDetailLoading(true);
    setSelectedPackId(packId);
    setInvoiceStatus('idle');
    try {
      const res = await api.get<ConversationDetail>(
        `/mercadolibre/messages/conversations/${packId}?seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        setDetail(res.data);
        setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch { /* silenced */ }
    setDetailLoading(false);
  }, [sellerId]);

  // ── Send message ──
  const handleSend = useCallback(async () => {
    if (!sellerId || !selectedPackId || !msgText.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/mercadolibre/messages/conversations/${selectedPackId}/send?seller_id=${sellerId}`, {
        text: msgText.trim(),
      });
      if (res.success) {
        setMsgText('');
        loadDetail(selectedPackId);
      }
    } catch { /* silenced */ }
    setSending(false);
  }, [sellerId, selectedPackId, msgText, loadDetail]);

  // ── Send invoice ──
  const handleSendInvoice = useCallback(async () => {
    if (!sellerId || !selectedPackId) return;
    setInvoiceSending(true);
    setInvoiceStatus('idle');
    try {
      const res = await api.post(`/mercadolibre/messages/conversations/${selectedPackId}/send-invoice?seller_id=${sellerId}`, {});
      if (res.success) {
        setInvoiceStatus('sent');
        loadDetail(selectedPackId);
      } else {
        setInvoiceStatus('error');
        alert('Error enviando invoice: ' + ((res as any).error || 'Unknown'));
      }
    } catch { setInvoiceStatus('error'); }
    setInvoiceSending(false);
  }, [sellerId, selectedPackId, loadDetail]);

  // ── Mark as read ──
  const markAsRead = useCallback(async (packId: number) => {
    if (!sellerId) return;
    try {
      await api.post(`/mercadolibre/messages/conversations/${packId}/mark-read?seller_id=${sellerId}`, {});
      setConversations(prev => prev.map(c =>
        c.pack_id === packId ? { ...c, unread_count: 0 } : c
      ));
      setUnreadTotal(prev => Math.max(0, prev - (conversations.find(c => c.pack_id === packId)?.unread_count || 0)));
    } catch { /* silenced */ }
  }, [sellerId, conversations]);

  // ── Effects ──
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // SSE refresh
  const loadRef = useRef(loadConversations);
  loadRef.current = loadConversations;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('messages:new', refresh),
      subscribe('ml:message', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  // Select conversation + mark read
  const selectConversation = useCallback((packId: number) => {
    loadDetail(packId);
    const conv = conversations.find(c => c.pack_id === packId);
    if (conv && conv.unread_count > 0) {
      markAsRead(packId);
    }
  }, [loadDetail, conversations, markAsRead]);

  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const selectedConv = conversations.find(c => c.pack_id === selectedPackId);
  const buyerId = detail?.order?.buyer_id || selectedConv?.buyer_id;

  // Mobile: back to list
  const handleMobileBack = useCallback(() => {
    setSelectedPackId(null);
    setDetail(null);
  }, []);

  // On mobile, hide header when viewing detail
  const showMobileDetail = isMobile && selectedPackId !== null;

  return (
    <div className="space-y-4">
      {/* Header — hidden on mobile when viewing detail */}
      {!showMobileDetail && (
        <div className="flex items-center gap-3">
          <Mail size={24} className="text-[var(--color-primary)]" />
          <h1 className="text-xl font-semibold text-[var(--color-text)]">{t('nav.messages')}</h1>
          {unreadTotal > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
              {unreadTotal}
            </span>
          )}
        </div>
      )}

      {/* Two-panel layout (desktop) / Stacked layout (mobile) */}
      <div className={`flex gap-4 ${isMobile ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-140px)]'}`}>
        {/* Left panel — conversation list (hidden on mobile when viewing detail) */}
        <div className={`${isMobile ? (showMobileDetail ? 'hidden' : 'w-full') : 'w-[380px] shrink-0'} flex flex-col bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl overflow-hidden`}>
          {/* Filter bar */}
          <div className="flex items-center gap-2 p-3 border-b border-[var(--color-border)]">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t('common.all', 'Todos')} ({total})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t('common.unread', 'No leídos')} ({unreadTotal})
            </button>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {loading && !conversations.length ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-muted)]" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-sm text-[var(--color-text-muted)]">
                {filter === 'unread' ? t('msg.noUnread', 'Sin mensajes no leídos') : t('msg.noConversations', 'Sin conversaciones')}
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.pack_id}
                  onClick={() => selectConversation(conv.pack_id)}
                  className={`flex items-start gap-3 p-3 cursor-pointer border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors ${
                    selectedPackId === conv.pack_id ? 'bg-[var(--color-primary-light)] border-l-[3px] border-l-[var(--color-primary)]' : ''
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] shrink-0 overflow-hidden">
                    {conv.item_thumbnail ? (
                      <img src={conv.item_thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-4 h-4 text-[var(--color-text-muted)]" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[var(--color-text)] truncate">
                        {conv.buyer_nickname}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)] shrink-0 ml-2">
                        {formatRelativeDate(conv.last_message_date || conv.last_updated)}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">
                      {conv.item_title || `#${conv.ml_order_id}`}
                    </div>
                    {conv.last_message_text && (
                      <div className="text-xs text-[var(--color-text-muted)] truncate mt-0.5 italic">
                        {conv.last_message_from !== conv.buyer_id ? 'Tú: ' : ''}{conv.last_message_text}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        conv.status === 'paid' ? 'bg-green-500/10 text-green-500' :
                        conv.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                        'bg-yellow-500/10 text-yellow-500'
                      }`}>
                        {conv.status}
                      </span>
                      {conv.context_site && (
                        <span className="text-xs text-[var(--color-text-muted)]">{conv.context_site}</span>
                      )}
                    </div>
                  </div>

                  {/* Unread badge */}
                  {conv.unread_count > 0 && (
                    <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium shrink-0">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right panel — conversation detail (hidden on mobile when no selection) */}
        <div className={`${isMobile ? (showMobileDetail ? 'w-full' : 'hidden') : 'flex-1'} flex flex-col bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl overflow-hidden`}>
          {!selectedPackId ? (
            <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)]">
              <div className="text-center">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t('msg.selectConversation', 'Selecciona una conversación')}</p>
              </div>
            </div>
          ) : detailLoading && !detail ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-text-muted)]" />
            </div>
          ) : detail ? (
            <>
              {/* Detail header */}
              <div className="p-3 border-b border-[var(--color-border)] flex items-center gap-3">
                {isMobile && (
                  <button
                    onClick={handleMobileBack}
                    className="p-1.5 -ml-1 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] shrink-0"
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--color-text)] truncate">
                    {detail.order.buyer_nickname}
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] truncate">
                    Orden #{detail.order.ml_order_id} · {detail.order.context_site}{can('global.view_money') ? ` · $${Number(detail.order.paid_amount).toFixed(2)} ${detail.order.currency_id}` : ''}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {detail.claim && (
                    <span className="px-2 py-0.5 bg-red-500/10 text-red-500 text-xs rounded-full flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {detail.claim.type}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    detail.order.status === 'paid' ? 'bg-green-500/10 text-green-500' :
                    detail.order.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                    'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {detail.order.status}
                  </span>
                </div>
              </div>

              {/* Context panel (collapsible) */}
              <div className="border-b border-[var(--color-border)] max-h-[280px] overflow-y-auto">
                {/* Sale section */}
                <CollapsibleSection
                  title={t('msg.sale', 'Venta ML')}
                  icon={<Package className="w-3.5 h-3.5" />}
                  expanded={expandedSection === 'sale'}
                  onToggle={() => toggleSection('sale')}
                >
                  <div className="space-y-1.5 text-xs">
                    {detail.order.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {item.thumbnail && <img src={item.thumbnail} className="w-8 h-8 rounded border border-[var(--color-border)]" />}
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-[var(--color-text)]">{item.title || item.item_id}</div>
                          <div className="text-[var(--color-text-muted)]">
                            {item.quantity}x{can('global.view_money') ? ` $${Number(item.unit_price).toFixed(2)} · Fee: $${Number(item.sale_fee || 0).toFixed(2)}` : ''} · SKU: {item.seller_sku}
                          </div>
                        </div>
                      </div>
                    ))}
                    {detail.shipment && (
                      <div className="text-[var(--color-text-muted)]">
                        Envío: <span className="text-[var(--color-text)]">{detail.shipment.status}</span>
                        {detail.shipment.tracking_number && ` · ${detail.shipment.tracking_number}`}
                      </div>
                    )}
                    {detail.profit !== null && can('global.view_money') && (
                      <div className="text-[var(--color-text-muted)]">
                        Profit: <span className={detail.profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                          ${detail.profit.toFixed(2)} ({detail.margin_pct}%)
                        </span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>

                {/* Purchase section */}
                {(detail.amazon_purchase || detail.stock_po) && (
                  <CollapsibleSection
                    title={t('msg.purchase', 'Compra')}
                    icon={<ShoppingCart className="w-3.5 h-3.5" />}
                    expanded={expandedSection === 'purchase'}
                    onToggle={() => toggleSection('purchase')}
                  >
                    <div className="space-y-1.5 text-xs text-[var(--color-text-muted)]">
                      {detail.amazon_purchase && (
                        <>
                          <div>ASIN: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.asin}</span></div>
                          <div>Status: <span className="text-[var(--color-text)]">{detail.amazon_purchase.amazon_status}</span></div>
                          {detail.amazon_purchase.total_cost && can('global.view_money') && (
                            <div>Costo: <span className="text-[var(--color-text)]">${Number(detail.amazon_purchase.total_cost).toFixed(2)}</span></div>
                          )}
                          {detail.amazon_purchase.tracking_number && (
                            <div>Tracking: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.tracking_number}</span> ({detail.amazon_purchase.carrier})</div>
                          )}
                          {detail.amazon_purchase.amazon_order_id && (
                            <div>Amazon Order: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.amazon_order_id}</span></div>
                          )}
                        </>
                      )}
                      {detail.stock_po && (
                        <div>PO: <span className="text-[var(--color-text)]">{detail.stock_po.po_number}</span> · {detail.stock_po.status}/{detail.stock_po.fulfillment_status}</div>
                      )}
                      {detail.amazon_product && (
                        <div className="pt-1 border-t border-[var(--color-border)]">
                          <div className="truncate">{detail.amazon_product.title}</div>
                          {detail.amazon_product.brand && <div>Brand: {detail.amazon_product.brand}</div>}
                          {detail.amazon_product.price && can('global.view_money') && <div>AMZ Price: ${Number(detail.amazon_product.price).toFixed(2)}</div>}
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Buyer history */}
                {(detail.buyer_questions.length > 0 || detail.buyer_orders.length > 0) && (
                  <CollapsibleSection
                    title={t('msg.buyerHistory', 'Historial buyer')}
                    icon={<MessageCircle className="w-3.5 h-3.5" />}
                    expanded={expandedSection === 'history'}
                    onToggle={() => toggleSection('history')}
                  >
                    <div className="space-y-2 text-xs">
                      {detail.buyer_questions.length > 0 && (
                        <div>
                          <div className="font-medium text-[var(--color-text)] mb-1">{t('msg.prevQuestions', 'Preguntas previas')} ({detail.buyer_questions.length})</div>
                          {detail.buyer_questions.slice(0, 5).map(q => (
                            <div key={q.question_id} className="mb-1 text-[var(--color-text-muted)]">
                              <div>Q: {q.text}</div>
                              {q.answer_text && <div className="ml-2 text-[var(--color-text)]">A: {q.answer_text}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {detail.buyer_orders.length > 0 && (
                        <div>
                          <div className="font-medium text-[var(--color-text)] mb-1">{t('msg.otherOrders', 'Otras órdenes')} ({detail.buyer_orders.length})</div>
                          {detail.buyer_orders.slice(0, 5).map(o => (
                            <div key={o.ml_order_id} className="flex items-center gap-2 text-[var(--color-text-muted)]">
                              <span className="font-mono">#{o.ml_order_id}</span>
                              <span className={o.status === 'paid' ? 'text-green-500' : 'text-red-500'}>{o.status}</span>
                              <Can cap="global.view_money"><span>${Number(o.paid_amount).toFixed(2)} {o.currency_id}</span></Can>
                              <span className="truncate flex-1">{o.item_title}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                )}
              </div>

              {/* Message thread — oldest first (chronological) */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {!detail.messages?.messages?.length ? (
                  <div className="text-center text-sm text-[var(--color-text-muted)] py-8">
                    {t('msg.noMessages', 'Sin mensajes en esta conversación')}
                  </div>
                ) : (
                  [...detail.messages.messages].reverse().map((msg, i) => {
                    const isBuyer = msg.from.user_id === buyerId;
                    const siteId = detail.order.context_site?.toUpperCase() || 'MLA';
                    return (
                      <div key={i} className={`p-3 rounded-lg text-sm ${
                        isBuyer
                          ? 'bg-[var(--color-bg)] border border-[var(--color-border)] mr-4 sm:mr-12'
                          : 'bg-blue-500/10 border border-blue-500/20 ml-4 sm:ml-12'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-[var(--color-text-muted)]">
                            {isBuyer ? 'Buyer' : 'Seller'}
                          </span>
                          <span className="text-xs text-[var(--color-text-muted)]">
                            {new Date(msg.message_date.created).toLocaleString()}
                          </span>
                          {!isBuyer && (
                            msg.message_date.read
                              ? <span className="text-xs text-blue-500" title={`Leído: ${new Date(msg.message_date.read).toLocaleString()}`}>✓✓</span>
                              : <span className="text-xs text-[var(--color-text-muted)]">✓</span>
                          )}
                        </div>
                        <div className="text-[var(--color-text)]">{msg.text}</div>
                        {msg.text_translated && (
                          <div className="text-xs text-[var(--color-text-muted)] mt-1 italic">{msg.text_translated}</div>
                        )}
                        {msg.message_attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.message_attachments.map((att, j) => {
                              const isImage = att.type?.startsWith('image/');
                              const proxyUrl = `/api/mercadolibre/messages/attachments/${encodeURIComponent(att.filename)}?seller_id=${sellerId}&site_id=${siteId}`;
                              return isImage ? (
                                <a key={j} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                   className="block max-w-[320px] rounded-lg overflow-hidden border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors group">
                                  <img src={proxyUrl} alt={att.original_filename || att.filename}
                                       className="w-full h-auto max-h-[400px] object-contain bg-black/5" loading="lazy" />
                                  <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--color-bg)] text-xs text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)]">
                                    <Download className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{att.original_filename || att.filename}</span>
                                    <span className="shrink-0 ml-auto">{(att.size / 1024).toFixed(0)} KB</span>
                                  </div>
                                </a>
                              ) : (
                                <a key={j} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                   className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary)] bg-[var(--color-bg)] transition-colors text-xs">
                                  <Download className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                                  <span className="text-[var(--color-text)]">{att.original_filename || att.filename}</span>
                                  <span className="text-[var(--color-text-muted)]">({(att.size / 1024).toFixed(0)} KB)</span>
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={threadEndRef} />
              </div>

              {/* Input area */}
              {detail.messages?.conversation_status?.status === 'active' ? (
                /* Sin ninguna de las dos llaves no queda ni la franja vacía. */
                <Can any={['messages.send', 'messages.send_invoice']}>
                <div className="p-3 border-t border-[var(--color-border)]">
                  <Can cap="messages.send">
                  <div className="flex gap-2">
                    <textarea
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                      placeholder={t('ml.o.messagePlaceholder', 'Escribe un mensaje...')}
                      className="flex-1 p-2.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none"
                      rows={2}
                    />
                    <button
                      onClick={handleSend}
                      disabled={sending || !msgText.trim()}
                      className="self-end px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Send className="w-4 h-4" />
                      {sending ? '...' : t('ml.o.sendMessage', 'Enviar')}
                    </button>
                  </div>
                  </Can>
                  <Can cap="messages.send_invoice">
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSendInvoice}
                      disabled={invoiceSending || invoiceStatus === 'sent'}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                        invoiceStatus === 'sent'
                          ? 'bg-green-500/20 text-green-600 border border-green-500/30'
                          : 'bg-amber-500/10 text-amber-600 border border-amber-500/30 hover:bg-amber-500/20'
                      } disabled:opacity-50`}
                    >
                      {invoiceSending ? (
                        <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
                      ) : invoiceStatus === 'sent' ? (
                        <><DollarSign className="w-3.5 h-3.5" /> Invoice Enviado</>
                      ) : (
                        <><DollarSign className="w-3.5 h-3.5" /> Send Invoice</>
                      )}
                    </button>
                  </div>
                  </Can>
                </div>
                </Can>
              ) : detail.messages?.conversation_status?.status === 'blocked' ? (
                <div className="p-3 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)] bg-[var(--color-bg)]">
                  Conversación bloqueada: {detail.messages.conversation_status.substatus || 'blocked'}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── CollapsibleSection component ──

function CollapsibleSection({
  title, icon, expanded, onToggle, children
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-[var(--color-border)] last:border-b-0">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-bg)] transition-colors"
      >
        {icon}
        {title}
        <span className="ml-auto">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      {expanded && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
}

// ── Helpers ──

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'ahora';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}
